/* global assignToVenuesUrl assignToVenuesUpdateUrl packExpenseablesUrl updatePackExpenseablesUrl storePRUrl dataUrl ACCEPTED_ATTACHMENT_FILE_TYPES */

import React, { Fragment, useEffect, useRef, useState } from 'react';

import { FieldArray, Formik } from 'formik';
import * as Yup from 'yup';

import DangerAlert from '../partial/alerts/DangerAlert';

import KeyDatesInputsGrid from './KeyDatesInputsGrid';
import ProcurementAttachments from './ProcurementAttachments';
import ProcurementExpenseEditModal from './ProcurementExpenseEditModal';
import ProcurementExpensesForm from './ProcurementExpensesForm';
import ProcurementExpenseTable from './ProcurementExpenseTable';
import ProcurementPackExpenseEditModal from './ProcurementPackExpenseEditModal';
import TendererField from './TendererField';

import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';
import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import FormHorizontalSeparator from 'CommonComponents/FormHorizontalSeparator';
import FormSectionHeading from 'CommonComponents/FormSectionHeading';
import MultiUserSelectorInput from 'CommonComponents/MultiUserSelectorInput';
import SelectFromField from 'CommonComponents/SelectFromField';
import TextareaFormField from 'CommonComponents/TextareaFormField';
import TextFormField from 'CommonComponents/TextFormField';
import UserSelectorInput from 'CommonComponents/UserSelectorInput';

import useModal from 'Hooks/useModal';
import { useUsers } from 'Hooks/useUsers';

import MinusIcon from 'Assets/icons/minus.svg';
import PlusIcon from 'Assets/icons/plus.svg';

const DEFAULT_TENDERER = { company_name: '', website: '', contact_name: '', contact_email: '' };

const ProcurementSchema = Yup.object().shape({
    name: Yup.string(),
    description: Yup.string().max(100),
    priority: Yup.number().required($t('This value is required')),
    complexity: Yup.number(),
    additional_category: Yup.string(),
    pr_number: Yup.string(),
    po_number: Yup.string(),
    target_scope_issue_to_market_date: Yup.date().required($t('This value is required')),
    expected_scope_issue_to_market_date: Yup.date(),
    actual_scope_issue_to_market_date: Yup.date(),
    target_response_due_date: Yup.date(),
    expected_response_due_date: Yup.date(),
    actual_response_due_date: Yup.date(),
    target_decision_date: Yup.date(),
    expected_decision_date: Yup.date(),
    actual_decision_date: Yup.date(),
    target_contract_award_date: Yup.date().required($t('This value is required')),
    expected_contract_award_date: Yup.date(),
    actual_contract_award_date: Yup.date(),
    target_contract_completion_date: Yup.date(),
    expected_contract_completion_date: Yup.date(),
    actual_contract_completion_date: Yup.date(),
    owner_id: Yup.string(),
    policy_id: Yup.number().required($t('This value is required')),
    category_id: Yup.number().required($t('This value is required')),
    procurement_status_id: Yup.number(),
    tenderers: Yup.array().of(
        Yup.object().shape({
            company_name: Yup.string(),
            website: Yup.string(),
            contact_name: Yup.string(),
            contact_email: Yup.string(),
        }),
    ),
    evaluator_ids: Yup.array().of(Yup.number()),
    attachments: Yup.mixed(),
});

export default function ProcurementEditForm(props) {
    const [showTemplateAssign, setShowTemplateAssign] = useState(false);
    const [showPackTable, setShowPackTable] = useState(false);
    const [showExpenseSelection, setShowExpenseSelection] = useState(false);
    const [error, setError] = useState(null);
    const [templateAssignUrl, setTemplateAssignUrl] = useState(assignToVenuesUrl);
    const [templateAssignUpdateUrl, setTemplateAssignUpdateUrl] = useState(assignToVenuesUpdateUrl);
    const [packProcurementUrl, setPackProcurementUrl] = useState(packExpenseablesUrl);
    const [updatePackProcurementUrl, setUpdatePackProcurementUrl] = useState(updatePackExpenseablesUrl);
    const [packTableUrl, setPackTableUrl] = useState(dataUrl);

    const validateFormButton = useRef(null);

    const procurementExpenseEditModal = useModal({ options: modalOptions(false, 'tw-z-60') });
    const procurementPackExpenseEditModal = useModal({ options: modalOptions(false, 'tw-z-60') });

    const { users } = useUsers();

    const syncExpenses = (expenseIds) => {
        const url = props.syncExpensesUrl.replace('ITEM_ID', props.procurement.id);
        return axios.post(url, { expense_ids: expenseIds });
    };

    const onExpenseEdit = (expense, groupKey = null) => {
        if (expense.type == 'template_child') {
            let newTemplateAssignUrl = templateAssignUrl.replace('ITEM_ID', props.procurement.id);
            newTemplateAssignUrl = newTemplateAssignUrl.replace('EXPENSE_ID', expense.id);
            setTemplateAssignUrl(newTemplateAssignUrl);

            let newTemplateAssignUpdateUrl = templateAssignUpdateUrl.replace('ITEM_ID', props.procurement.id);
            newTemplateAssignUpdateUrl = newTemplateAssignUpdateUrl.replace('EXPENSE_ID', expense.id);
            setTemplateAssignUpdateUrl(newTemplateAssignUpdateUrl);

            procurementExpenseEditModal.show();
            setShowTemplateAssign(true);
        } else {
            let tempPackTableUrl = packTableUrl.replace('EXPENSE_ID', groupKey);
            tempPackTableUrl = tempPackTableUrl.replace('ITEM_ID', expense.id);
            setPackTableUrl(tempPackTableUrl);

            let newPackProcurementUrl = packProcurementUrl.replace('ITEM_ID', props.procurement.id);
            newPackProcurementUrl = newPackProcurementUrl.replace('EXPENSE_ID', groupKey);
            setPackProcurementUrl(newPackProcurementUrl);

            let newUpdatePackProcurementUrl = updatePackProcurementUrl.replace('ITEM_ID', props.procurement.id);
            newUpdatePackProcurementUrl = newUpdatePackProcurementUrl.replace('EXPENSE_ID', groupKey);
            setUpdatePackProcurementUrl(newUpdatePackProcurementUrl);

            procurementPackExpenseEditModal.show();
            setShowPackTable(true);
        }
    };

    const closeHandler = () => {
        setTemplateAssignUrl(assignToVenuesUrl);
        setTemplateAssignUpdateUrl(assignToVenuesUpdateUrl);
        setShowTemplateAssign(false);
        setShowPackTable(false);
        setTimeout(() => {
            props.onRefresh(props.procurement.id);
        }, 1);
    };

    const onExpenseRemove = (expenseId) => {
        const expenseIds = props.procurement.expense_ids.filter((id) => id !== expenseId);

        syncExpenses(expenseIds)
            .then((res) => {
                props.onExpensesSynced(props.procurement.id);
            })
            .catch((e) => {
                console.error(e);
            });
    };

    const onExpensesAdd = (expenseIds) => {
        syncExpenses([...props.procurement.expense_ids, ...expenseIds])
            .then((res) => {
                setShowExpenseSelection(false);
                props.onExpensesSynced(props.procurement.id);
            })
            .catch((e) => {
                console.error(e);
            });
    };

    const deleteAttachment = (attachment) => {
        if (!confirm($t('Are you sure you want delete this attachment?'))) {
            return;
        }

        const url = props.deleteAttachmentUrl.replace('ITEM_ID', props.procurement?.id).replace('ATTACHMENT_ID', attachment.id);

        axios
            .delete(url)
            .then(() => props.onAttachmentDeleted(props.procurement.id))
            .catch(() => setRequestFailed(true));
    };

    const onPrConfirm = () => {
        props.setLoading('data', true);
        const url = storePRUrl.replace('ITEM_ID', props.procurement.id);

        axios
            .post(
                url,
                {
                    procurement_policy_id: props.procurement.policy_id,
                    title: props.procurement.name,
                    estimated_value: props.procurement.value || 0,
                },
                {
                    headers: { ...mutationHeader },
                },
            )
            .then(() => {
                window.location.href = props.indexPRUrl;
            })
            .catch(() => setRequestFailed(true))
            .finally(() => props.setLoading('data', false));
    };

    useEffect(() => {
        if (validateFormButton.current) {
            validateFormButton.current.click();
        }
    }, []);

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={{
                    name: props.procurement?.name || '',
                    description: props.procurement?.description || '',
                    owner_id: props.procurement?.owner_id || '',
                    priority: props.procurement?.priority?.toString() || '',
                    complexity: props.procurement?.complexity?.toString() || '',
                    additional_category: props.procurement?.additional_category || '',
                    pr_number: props.procurement?.pr_number || '',
                    po_number: props.procurement?.po_number || '',
                    target_scope_issue_to_market_date: props.procurement?.target_scope_issue_to_market_date || '',
                    expected_scope_issue_to_market_date: props.procurement?.expected_scope_issue_to_market_date || '',
                    actual_scope_issue_to_market_date: props.procurement?.actual_scope_issue_to_market_date || '',
                    target_response_due_date: props.procurement?.target_response_due_date || '',
                    expected_response_due_date: props.procurement?.expected_response_due_date || '',
                    actual_response_due_date: props.procurement?.actual_response_due_date || '',
                    target_decision_date: props.procurement?.target_decision_date || '',
                    expected_decision_date: props.procurement?.expected_decision_date || '',
                    actual_decision_date: props.procurement?.actual_decision_date || '',
                    target_contract_award_date: props.procurement?.target_contract_award_date || '',
                    expected_contract_award_date: props.procurement?.expected_contract_award_date || '',
                    actual_contract_award_date: props.procurement?.actual_contract_award_date || '',
                    target_contract_completion_date: props.procurement?.target_contract_completion_date || '',
                    expected_contract_completion_date: props.procurement?.expected_contract_completion_date || '',
                    actual_contract_completion_date: props.procurement?.actual_contract_completion_date || '',
                    policy_id: props.procurement?.policy_id || '',
                    category_id: props.procurement?.category_id || '',
                    procurement_status_id: props.procurement?.procurement_status_id || '',
                    tenderers: props.procurement?.tenderers || [],
                    evaluator_ids: props.procurement?.evaluator_ids || [],
                    attachments: props.procurement?.attachments || [],
                }}
                validationSchema={ProcurementSchema}
                initialTouched={{
                    priority: true,
                    target_scope_issue_to_market_date: true,
                    target_contract_award_date: true,
                    policy_id: true,
                    category_id: true,
                }}
                onSubmit={(values, actions) => {
                    setError(null);
                    const url = props.saveUrl.replace('ITEM_ID', props.procurement?.id);
                    const formData = new FormData();

                    Object.entries(values).forEach(([key, value]) => {
                        if (['tenderers', 'evaluator_ids'].includes(key)) {
                            formData.append(key, JSON.stringify(value));
                        } else if (key === 'attachments') {
                            value.forEach((attachment) => {
                                formData.append('attachments[]', attachment);
                            });
                        } else {
                            formData.append(key, value);
                        }
                    });

                    axios
                        .post(url, formData, {
                            params: { _method: 'PUT' }, // This is a workaround to be able to send multipart/form-data as put request
                            headers: { 'Content-Type': 'multipart/form-data' },
                        })
                        .then((res) => {
                            actions.setSubmitting(false);
                            props.onSuccess();
                        })
                        .catch((e) => {
                            e?.response?.data?.errors && actions.setErrors(e.response.data.errors);
                            setError(e?.response?.data?.errors);
                            actions.setSubmitting(false);
                        });
                }}
            >
                {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue, validateForm }) => (
                    <>
                        <div
                            style={{ display: !showExpenseSelection ? 'block' : 'none' }}
                            className="tw-transition tw-ease-in-out tw-delay-150 tw-duration-300 modal__form-container"
                        >
                            {!props.hideDetails && (
                                <Fragment>
                                    <FormSectionHeading heading={$t('Summary')}></FormSectionHeading>
                                    <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                                        <div className="tw-flex tw-flex-col tw-space-y-4">
                                            <TextFormField label={$t('Name')} type="text" name="name" disabled={props.isCreatePrMode}></TextFormField>
                                            <TextareaFormField
                                                label={$t('Description')}
                                                as="textarea"
                                                type="text"
                                                name="description"
                                                placeholder={$t('maximum 100 characters')}
                                                disabled={props.isCreatePrMode}
                                            ></TextareaFormField>
                                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                <label className="text-input__label">{$t('Value')}</label>
                                                <input
                                                    type="text"
                                                    className="tw-border-0 text-input"
                                                    value={formatToFixedMoneyWithLocale(props.procurement?.value) || ''}
                                                    disabled
                                                />
                                            </div>
                                            <CategorySelector
                                                name="category_id"
                                                categories={props.categories}
                                                errors={errors.category_id}
                                                touched={touched.category_id}
                                                disabled={props.isCreatePrMode}
                                            />
                                            <TextFormField
                                                label={$t('Additional Procurement Category ')}
                                                type="text"
                                                name="additional_category"
                                                disabled={props.isCreatePrMode}
                                            ></TextFormField>
                                            <TextFormField label={$t('PR Number')} type="text" name="pr_number" disabled={props.isCreatePrMode}></TextFormField>
                                        </div>
                                        <div className="tw-flex tw-flex-col tw-space-y-4">
                                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                <label className="text-input__label">{$t('Owner')}</label>
                                                <UserSelectorInput
                                                    selectedId={values.owner_id}
                                                    usersList={users}
                                                    onChange={(value) => setFieldValue('owner_id', value)}
                                                    disabled={props.isCreatePrMode}
                                                />
                                                {errors.owner_id && touched.owner_id ? (
                                                    <div className="tw-text-red-500 tw-text-sm">{errors.owner_id}</div>
                                                ) : null}
                                            </div>
                                            <StatusSelector
                                                name="procurement_status_id"
                                                statuses={props.statuses}
                                                errors={errors.procurement_status_id}
                                                touched={touched.procurement_status_id}
                                                disabled={props.isCreatePrMode}
                                            />
                                            <PolicySelector
                                                name="policy_id"
                                                policies={props.policies}
                                                errors={errors.policy_id}
                                                touched={touched.policy_id}
                                                disabled={props.isCreatePrMode}
                                                showInfo={() => props.showInfo()}
                                            />
                                            <PrioritySelector
                                                name="priority"
                                                priorities={props.priorities}
                                                errors={errors.priority}
                                                touched={touched.priority}
                                                disabled={props.isCreatePrMode}
                                            />
                                            <ComplexitySelector
                                                name="complexity"
                                                complexities={props.complexities}
                                                errors={errors.complexity}
                                                touched={touched.complexity}
                                                disabled={props.isCreatePrMode}
                                            />
                                            <TextFormField label={$t('EA Number')} type="text" name="po_number" disabled={props.isCreatePrMode}></TextFormField>
                                        </div>
                                    </div>

                                    {/* Budget Line Items Section */}
                                    <FormHorizontalSeparator disabled={props.isCreatePrMode} />
                                    <div className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                                        <FormSectionHeading heading={$t('Budget Line Items')}></FormSectionHeading>
                                        <Button
                                            small
                                            onClick={() => setShowExpenseSelection(true)}
                                            disabled={props.isCreatePrMode || props.procurement?.is_completed}
                                        >
                                            <PlusIcon className="tw-w-3 tw-h-3 tw-mr-2" /> {$t('Item')}
                                        </Button>
                                    </div>

                                    {/* Expenses Section */}
                                    <FormHorizontalSeparator disabled={props.isCreatePrMode} />

                                    <ProcurementExpenseTable
                                        expensesTree={props.procurement?.expenses_tree}
                                        total={props.procurement?.expenses_tree?.total}
                                        maxQuantities={props.procurement?.expenses_tree?.max_quantities}
                                        onRemove={!props.isCreatePrMode && onExpenseRemove}
                                        onEdit={!props.isCreatePrMode && onExpenseEdit}
                                        viewOnly={props.procurement?.is_completed}
                                        removable
                                        lockedExpenseIds={props.lockedExpenseIds}
                                    />

                                    {/* Key Dates Section */}
                                    <FormHorizontalSeparator disabled={props.isCreatePrMode} />

                                    <KeyDatesInputsGrid
                                        values={values}
                                        errors={errors}
                                        touched={touched}
                                        setFieldValue={setFieldValue}
                                        disabled={props.isCreatePrMode}
                                        eventStartDate={props.eventStartDate}
                                        eventEndDate={props.eventEndDate}
                                    />

                                    {/* Tenderers Section */}
                                    <FormHorizontalSeparator disabled={props.isCreatePrMode} />

                                    <div className="tw-flex tw-flex-col tw-space-y-4">
                                        <FieldArray name="tenderers">
                                            {(arrayHelpers) => (
                                                <>
                                                    <div className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                                                        <FormSectionHeading heading={$t('Possible Tenderers')}></FormSectionHeading>
                                                        <Button small onClick={() => arrayHelpers.push(DEFAULT_TENDERER)} disabled={props.isCreatePrMode}>
                                                            <PlusIcon className="tw-w-3 tw-h-3 tw-mr-2" /> {$t('Company')}
                                                        </Button>
                                                    </div>

                                                    {!!values.tenderers?.length &&
                                                        values.tenderers.map((tenderer, index) => {
                                                            const tendererErrors = (errors?.tenderers || [])[index];
                                                            const tendererTouched = (touched?.tenderers || [])[index];

                                                            return (
                                                                <Fragment key={index}>
                                                                    <TendererField
                                                                        name={`tenderers.${index}`}
                                                                        errors={tendererErrors}
                                                                        touched={tendererTouched}
                                                                        disabled={props.isCreatePrMode}
                                                                        actions={
                                                                            <Button
                                                                                small
                                                                                variant="danger"
                                                                                className="mx-1"
                                                                                onClick={() => arrayHelpers.remove(index)}
                                                                                disabled={props.isCreatePrMode}
                                                                            >
                                                                                <MinusIcon className="tw-w-3.5 tw-h-3.5" />
                                                                            </Button>
                                                                        }
                                                                    />
                                                                </Fragment>
                                                            );
                                                        })}
                                                </>
                                            )}
                                        </FieldArray>
                                    </div>

                                    {/* Evaluators Section */}
                                    <FormHorizontalSeparator disabled={props.isCreatePrMode} />

                                    <div className="tw-grid tw-grid-cols-1">
                                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                            <label className="text-input__label" htmlFor="evaluator_ids">
                                                {$t('Evaluators')}
                                            </label>
                                            <MultiUserSelectorInput
                                                isMulti
                                                selectedIds={values.evaluator_ids}
                                                usersList={users}
                                                onChange={(value) => setFieldValue('evaluator_ids', value)}
                                                disabled={props.isCreatePrMode}
                                            />
                                            {errors.evaluator_ids && touched.evaluator_ids ? (
                                                <div className="tw-text-red-500 tw-text-sm">{errors.evaluator_ids}</div>
                                            ) : null}
                                        </div>
                                    </div>

                                    {/* Attachments Section */}
                                    <FormHorizontalSeparator disabled={props.isCreatePrMode} />

                                    <FormSectionHeading heading={$t('Attachments')}></FormSectionHeading>
                                    <div className="tw-grid tw-grid-cols-1">
                                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                            <label htmlFor="attachments" className="text-input__label">
                                                {$t('Select Attachments')}
                                            </label>
                                            <input
                                                id="attachments-input"
                                                type="file"
                                                className={
                                                    errors.attachments && touched.attachments
                                                        ? 'tw-border tw-border-red-500 file-input'
                                                        : 'tw-border-0 file-input'
                                                }
                                                name="attachments"
                                                multiple
                                                accept={ACCEPTED_ATTACHMENT_FILE_TYPES}
                                                disabled={props.isCreatePrMode}
                                                onChange={(event) => {
                                                    setFieldValue('attachments', Array.from(event.currentTarget.files));
                                                }}
                                            />
                                            {errors.attachments && touched.attachments && (
                                                <div className="tw-text-red-500 tw-text-sm">{errors.attachments}</div>
                                            )}
                                        </div>
                                    </div>
                                    {!!props.procurement?.attachments?.length && (
                                        <ProcurementAttachments
                                            procurement={props.procurement}
                                            attachments={props.procurement.attachments}
                                            onDelete={deleteAttachment}
                                            disabled={props.isCreatePrMode}
                                            canDelete={true}
                                            downloadAttachmentUrl={props.downloadAttachmentUrl}
                                        />
                                    )}

                                    {/* Error Section */}
                                    {!!error && _.isObject(error) && (
                                        <>
                                            {Object.values(error)[0].map((e, i) => {
                                                return <DangerAlert key={i} message={e} />;
                                            })}
                                        </>
                                    )}
                                    <div className="modal__footer">
                                        {!props.isCreatePrMode ? (
                                            <>
                                                <button className="tw-hidden" type="button" ref={validateFormButton} onClick={() => validateForm()}>
                                                    Validate All
                                                </button>
                                                <Button variant="danger" onClick={props.closeHandler} disabled={isSubmitting}>
                                                    {$t('Cancel')}
                                                </Button>
                                                <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                                                    {$t('Save')}
                                                </Button>
                                            </>
                                        ) : (
                                            <Button onClick={props.onEdit}>{$t('Edit')}</Button>
                                        )}
                                    </div>
                                </Fragment>
                            )}
                        </div>
                        <div
                            style={{ display: showExpenseSelection ? 'block' : 'none' }}
                            className="tw-transition tw-ease-in-out tw-delay-150 tw-duration-300 modal__form-container"
                        >
                            <ProcurementExpensesForm
                                data={props.expensesTree}
                                expensesIds={props.expensesIds}
                                existedIds={props.existedIds}
                                closeHandler={() => setShowExpenseSelection(false)}
                                setOrdering={props.setOrdering}
                                arrow={props.arrow}
                                onSubmit={onExpensesAdd}
                                onCancel={() => setShowExpenseSelection(false)}
                                lockedExpenseIds={props.lockedExpenseIds}
                            />
                        </div>
                    </>
                )}
            </Formik>
            <ProcurementExpenseEditModal
                id={`procurement-expense-edit-modal-${props.procurement?.id}`}
                modal={procurementExpenseEditModal}
                closeHandler={closeHandler}
                showTemplateAssign={showTemplateAssign}
                assignToVenuesUrl={templateAssignUrl}
                assignToVenuesUpdateUrl={templateAssignUpdateUrl}
            ></ProcurementExpenseEditModal>

            <ProcurementPackExpenseEditModal
                id={`procurement-pack-expense-edit-modal-${props.procurement?.id}`}
                modal={procurementPackExpenseEditModal}
                closeHandler={closeHandler}
                successHandler={closeHandler}
                showPackTable={showPackTable}
                packTableUrl={packTableUrl}
                packProcurementUrl={packProcurementUrl}
                updatePackProcurementUrl={updatePackProcurementUrl}
                procurement={props.procurement}
            ></ProcurementPackExpenseEditModal>
        </>
    );
}

function CategorySelector(props) {
    return (
        <SelectFromField label={$t('Procurement Category')} required={true} as="select" name={props.name} disabled={props.disabled}>
            <option value="">{$t('Choose Category')}</option>
            {props.categories?.map((category) => {
                return (
                    <option key={category.id} value={category.id}>
                        {category.name}
                    </option>
                );
            })}
        </SelectFromField>
    );
}

function StatusSelector(props) {
    return (
        <SelectFromField label={$t('Status')} as="select" name={props.name} disabled={props.disabled}>
            <option value="">{$t('Choose Status')}</option>
            {props.statuses?.map((status) => {
                return (
                    <option key={status.id} value={status.id}>
                        {status.title}
                    </option>
                );
            })}
        </SelectFromField>
    );
}

export function PolicySelector(props) {
    return (
        <SelectFromField
            label={!props.hideLabel ? $t('Procurement Policy') : null}
            required={true}
            as="select"
            name={props.name}
            disabled={props.disabled}
            showInfo={() => props.showInfo()}
        >
            <option value="">{$t('Choose Procurement Policy')}</option>
            {props.policies?.map((policy) => {
                return (
                    <option key={policy.id} value={policy.id}>
                        {policy.method}
                    </option>
                );
            })}
        </SelectFromField>
    );
}

export function PrioritySelector(props) {
    return (
        <SelectFromField label={!props.hideLabel ? $t('Priority') : null} required={true} as="select" name={props.name} disabled={props.disabled}>
            <option value="">{$t('Choose Priority')}</option>
            {props.priorities?.map((name, index) => {
                return (
                    <option key={index} value={index}>
                        {name}
                    </option>
                );
            })}
        </SelectFromField>
    );
}

export function ComplexitySelector(props) {
    return (
        <SelectFromField label={!props.hideLabel ? $t('Complexity') : null} required={true} as="select" name={props.name} disabled={props.disabled}>
            <option value="">{$t('Choose Complexity')}</option>
            {props.complexities?.map((name, index) => {
                return (
                    <option key={index} value={index}>
                        {name}
                    </option>
                );
            })}
        </SelectFromField>
    );
}
